import { ApolloCache } from '@apollo/client';

import { ListCacheUpdater, MutationCacheUpdate } from './types';

export type ListCreateMutationCacheConfig<
  TItem extends Record<string, unknown>,
  TScope,
  TData,
  TVariables,
> = {
  updater: ListCacheUpdater<TItem, TScope>;
  getCreatedItem: (data: TData | null | undefined) => TItem | null | undefined;
  getCreateScope: (item: TItem, variables: TVariables) => TScope;
};

export type ListUpdateMutationCacheConfig<
  TItem extends Record<string, unknown>,
  TScope,
  TData,
> = {
  updater: ListCacheUpdater<TItem, TScope>;
  getUpdatedItem: (data: TData | null | undefined) => TItem | null | undefined;
};

export type ListDeleteMutationCacheConfig<
  TItem extends Record<string, unknown>,
  TScope,
  TVariables,
> = {
  updater: ListCacheUpdater<TItem, TScope>;
  getDeletedName: (variables: TVariables) => string | undefined;
  getDeleteScope: (
    cache: ApolloCache<unknown>,
    deletedName: string,
    variables: TVariables,
    fallbackIdentityName?: string,
  ) => TScope;
  fallbackIdentityName?: string;
};

export const buildListCreateCacheUpdate = <
  TItem extends Record<string, unknown>,
  TScope,
  TData,
  TVariables,
>({
  updater,
  getCreatedItem,
  getCreateScope,
}: ListCreateMutationCacheConfig<
  TItem,
  TScope,
  TData,
  TVariables
>): MutationCacheUpdate<TData, TVariables> => {
  return (apolloCache, { data }, options) => {
    const created = getCreatedItem(data as TData | null | undefined);
    if (!created) {
      return;
    }

    const { variables } = options;
    if (!variables) {
      return;
    }

    const scope = getCreateScope(created, variables);

    if (!updater.appendCreated(apolloCache, created, scope)) {
      updater.invalidateAll(apolloCache);
    }
  };
};

export const buildListUpdateCacheUpdate = <
  TItem extends Record<string, unknown>,
  TScope,
  TData,
>({
  updater,
  getUpdatedItem,
}: ListUpdateMutationCacheConfig<
  TItem,
  TScope,
  TData
>): MutationCacheUpdate<TData> => {
  return (apolloCache, { data }) => {
    const updated = getUpdatedItem(data as TData | null | undefined);
    if (!updated) {
      return;
    }

    if (!updater.writeUpdated(apolloCache, updated)) {
      updater.invalidateAll(apolloCache);
    }
  };
};

export const buildListDeleteCacheUpdate = <
  TItem extends Record<string, unknown>,
  TScope,
  TVariables,
  TData = unknown,
>({
  updater,
  getDeletedName,
  getDeleteScope,
  fallbackIdentityName,
}: ListDeleteMutationCacheConfig<
  TItem,
  TScope,
  TVariables
>): MutationCacheUpdate<TData, TVariables> => {
  return (apolloCache, _result, options) => {
    const { variables } = options;
    if (!variables) {
      return;
    }

    const deletedName = getDeletedName(variables);
    if (!deletedName) {
      return;
    }

    const scope = getDeleteScope(
      apolloCache,
      deletedName,
      variables,
      fallbackIdentityName,
    );

    if (!updater.removeDeleted(apolloCache, deletedName, scope)) {
      updater.invalidateAll(apolloCache);
    }
  };
};
